import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import * as Models from '../../models';
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material";
import { IBenefitBasketData } from "../../models/bonusSystemSOT.facade";
import { SOTService } from "../../services";

/**
 * Configuration interface for BonusActionsDialogComponent
 */
export interface IBonusActionsDialogComponentConfig {
  bonusActions: Models.SOTBonusAction[];
  sot_service: SOTService;
}

@Component({
  selector: "sot-bonusActionsDialog",
  templateUrl: "./bonusActionsDialog.component.html",
  styleUrls: ["./bonusActionsDialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class BonusActionsDialogComponent implements OnInit, OnDestroy {

  private readonly _LIMIT_BENEFITS: number = 10;

  private _sotService: SOTService;

  public bonusActions: Models.SOTBonusAction[] = [];
  public benefitProduct: Models.SOTProductViewModel;

  public selectedBonusAction: Models.SOTBonusAction;
  public selectedBenefit: Models.SOTBonusActionBenefit;
  public selectedBenefitCost: number;

  public displayBenefitProductDetails: boolean = false;
  public showBenefitAdditionalInformation: boolean = false;

  private _showMoreActionIndices: number[] = [];

  constructor(private _changeDetectorRef: ChangeDetectorRef
    , public dialogRef: MatDialogRef<BonusActionsDialogComponent, any>
    , @Inject(MAT_DIALOG_DATA) public data: IBonusActionsDialogComponentConfig
  ) {
    const me = this;
    me.bonusActions = data.bonusActions;
    me._sotService = data.sot_service;
  }

  ngOnInit(): void { }

  ngOnDestroy(): void { }

  //#region Getters
  public get isModeOne(): boolean {
    const me = this;
    return me._sotService.Accessabilty.useModeOne;
  }

  public get isModeTwo(): boolean {
    const me = this;
    return me._sotService.Accessabilty.useModeTwo;
  }

  public get hasBenefitProductExtras(): boolean {
    const me = this;
    let extraGroups: Models.SOTProductExtraGroupViewModel[] = [];
    me.benefitProduct && me.benefitProduct.extraGroups ? extraGroups = me.benefitProduct.extraGroups : extraGroups = [];
    return extraGroups.filter(extragroup => !extragroup.hasNoChoice).length > 0;
  }
  //#endregion

  //#region Points & Validation

  /**
   * Check if benefit can be redeemed (user has enough available points)
   */
  public canRedeem(bonusAction: Models.SOTBonusAction, benefit: Models.SOTBonusActionBenefit, amount: number = 1): boolean {
    return bonusAction.canRedeem(benefit, amount);
  }


  /**
   * Calculate percentage of available points relative to benefit cost
   */
  public getAvailablePointsPercentage(benefit: Models.SOTBonusActionBenefit, bonusAction: Models.SOTBonusAction): number {
    return bonusAction.getProgressPercentage(benefit);
  }

  /**
   * Calculate maximum redeemable count based on available points
   * Returns at least the current count in basket to allow removing items
   */
  public getMaxRedeemableCount(bonusAction: Models.SOTBonusAction, benefit: Models.SOTBonusActionBenefit): number {
    const me = this;
    return bonusAction.getMaxRedeemable(benefit, me._sotService.GetBasket().products);
  }

  /**
   * Update total points in basket for the bonus action
   */
  private updatePointsInBasket(bonusAction: Models.SOTBonusAction): void {
    const me = this;
    bonusAction.updatePointsInBasket(me._sotService.GetBasket().products);
  }

  //#endregion

  //#region Basket Operations

  /**
   * Get current count of benefit in basket
   */
  public getBenefitCount(bonusAction: Models.SOTBonusAction, benefit: Models.SOTBonusActionBenefit): number {
    const me = this;
    return bonusAction.getCountInBasket(benefit, me._sotService.GetBasket().products);
  }

  /**
   * Handle benefit amount change (add/remove from basket)
   * @param newCount New count value
   * @param bonusAction The bonus action
   * @param benefit The benefit being changed
   */
  public onBenefitAmountChange(newCount: number, bonusAction: Models.SOTBonusAction, benefit: Models.SOTBonusActionBenefit): void {
    const me = this;
    const currentCount = me.getBenefitCount(bonusAction, benefit);
    const countDifference = newCount - currentCount;

    if (countDifference > 0) {
      me.prepareBenefitForDetail(bonusAction, benefit);
      if (!me.benefitProduct) {
        return;
      }
      if (me.hasBenefitProductExtras) {
        me.displayBenefitProductDetails = true;
        me.pushChanges();
      } else {
        me.addBenefitToBasket(bonusAction, benefit, countDifference, []);
      }
    } else if (countDifference < 0) {
      me.removeBenefitFromBasket(bonusAction, benefit, Math.abs(countDifference));
    }
  }

  /** Add benefit to basket after checking requirements (points availability). */
  public onAddToBasket(selectedExtraGroups: Models.SOTBasketItemExtraGroupViewModel[]) {
    const me = this;
    if (me.selectedBonusAction && me.selectedBenefit && me.canRedeem(me.selectedBonusAction, me.selectedBenefit, 1)) {
      me.addBenefitToBasket(me.selectedBonusAction, me.selectedBenefit, 1, selectedExtraGroups);
      me.displayBenefitProductDetails = false;
      me.pushChanges();
    }
  }

  /**
   * Adds the requested benefit quantity to the basket, including selected extras,
   * while ensuring the redeemable limit is not exceeded.
   */
  private addBenefitToBasket(
    bonusAction: Models.SOTBonusAction,
    benefit: Models.SOTBonusActionBenefit,
    count: number,
    selectedExtraGroups: Models.SOTBasketItemExtraGroupViewModel[]
  ): void {
    const me = this;
    const product = me.getBenefitProduct(benefit.productID);
    if (!product) {
      me._sotService.coreLogService.error("BonusActionsDialogComponent.addBenefitToBasket", "Benefit product not found", benefit.productID);
      return;
    }
    const benefitBasketData: IBenefitBasketData = {
      benefitId: benefit.ID,
      bonusactionId: benefit.bonusActionID,
      benefitProductId: benefit.productID,
      benefitLossId: benefit.lossID,
      benefitCostPoints: benefit.costPoints,
      benefitImageUrl: benefit.imageUrl,
      benefitHasImage: benefit.hasImage
    };
    const products = me._sotService.GetBasket().products;
    const stillAffordable = bonusAction.getMaxRedeemable(benefit, products) - bonusAction.getCountInBasket(benefit, products);
    const safeCount = Math.min(count, Math.max(0, stillAffordable));
    if (safeCount <= 0) { return; }
    const tempBasket = new Models.SOTBasketViewModel(me._sotService);
    for (let i = 0; i < safeCount; i++) {
      const basketProduct = Models.SOTBasketProductViewModel.createBenefitProduct(product, benefitBasketData);
      me._addExtrasToBenefitProduct(basketProduct, selectedExtraGroups);
      tempBasket.addBasketProduct(basketProduct);
    }
    me._sotService.GetBasket().merge(tempBasket);
    me.updatePointsInBasket(bonusAction);
    me.pushChanges();
  }

  /**
   * Adds all extras and their sub-extras to the benefit product
   * according to their respective quantities.
   */
  private _addExtrasToBenefitProduct(
    benefitProduct: Models.SOTBasketProductViewModel,
    extraGroups: Models.SOTBasketItemExtraGroupViewModel[]
  ): void {
    if (!extraGroups || extraGroups.length === 0) { return; }
    extraGroups.forEach(group => {
      group.extras.forEach(extra => {
        if (extra.sub_extras.length === 0) {
          for (let i = 0; i < extra.count; i++) {
            benefitProduct.addExtraGroup(group.query, extra.extra_id, 0);
          }
        } else {
          extra.sub_extras.forEach(subExtra => {
            for (let i = 0; i < subExtra.count; i++) {
              benefitProduct.addExtraGroup(group.query, extra.extra_id, subExtra.extra_id);
            }
          });
        }
      });
    });
  }

  /**
   * Remove benefit from basket and update points
   * If the same product is added multiple times with different extras
   * clicking "Remove" removes the **last added** instance first.
   */
  private removeBenefitFromBasket(bonusAction: Models.SOTBonusAction, benefit: Models.SOTBonusActionBenefit, count: number): void {
    const me = this;
    const basket = me._sotService.GetBasket();
    const benefitProducts = basket.products.filter(p =>
      p.isBenefitItem(p.benefitLossId, p.bonusactionId, p.benefitId) &&
      me._isSameBenefit(p, benefit)
    );

    //Reverse array to start from last added (newest first)
    const reversedProducts = [...benefitProducts].reverse();

    let remainingToRemove = count;
    reversedProducts.forEach(basketProduct => {
      if (remainingToRemove <= 0) return;

      if (basketProduct.count <= remainingToRemove) {
        // Remove entire product
        remainingToRemove -= basketProduct.count;
        basket.removeBasketProduct(basketProduct);
      } else {
        // Reduce count of this product
        basketProduct.count -= remainingToRemove;
        remainingToRemove = 0;
        basket.notifyBasketChanged();
      }
    });

    me.updatePointsInBasket(bonusAction);
    me.pushChanges();
  }

  //#endregion

  //#region Product Details & Additional Information

  /** Get benefit product by ID */
  private getBenefitProduct(benefitProductId: number): Models.SOTProductViewModel | null {
    const me = this;
    return me._sotService.getBenefitProduct(benefitProductId);
  }

  /**
   * Handle benefit card click - load benefit product and show details
   * @param bonusAction The bonus action
   * @param benefit The benefit being clicked
   * @param event Click event
   */
  public onBenefitClick(bonusAction: Models.SOTBonusAction, benefit: Models.SOTBonusActionBenefit, event: Event): void {
    const me = this;
    event.stopPropagation();
    me.prepareBenefitForDetail(bonusAction, benefit);
    if (!me.benefitProduct) {
      return;
    }
    me.displayBenefitProductDetails = true;
    me.pushChanges();
  }

  /**
   * Display the "i" button if the required settings are active and additional information is included
   * @param benefitProductId
   * @returns hasAdditionalInfo
   */
  public canDisplayAdditionalInformation(benefitProductId: number): boolean {
    const me = this;
    let hasAdditionalInfo: boolean = false;
    const settings = me._sotService.GetSettings();
    const benefitProduct = me.getBenefitProduct(benefitProductId);
    if (settings && benefitProduct) {
      hasAdditionalInfo = ((settings.use_co2 && benefitProduct.details.co2 > 0) ||
        (settings.use_nutrition_values && benefitProduct.details.nutritions.nutritionItemViewModels.length > 0) ||
        (settings.use_allergens && benefitProduct.details.allergens.length > 0) ||
        (settings.use_additives && benefitProduct.details.additives.length > 0) ||
        (settings.use_eCommerce && (benefitProduct.details.item_Content.length > 0 || benefitProduct.details.hasProvider_Info))
      )
    }
    return hasAdditionalInfo;
  }

  /**
   * Toggles the visibility of Extra additional information section
   */
  public toggleBenefitAdditionalInfo(show: boolean) {
    const me = this;
    me.showBenefitAdditionalInformation = show;
    me.pushChanges();
  }

  /** Display additional information for a benefit product */
  public displayAdditionalInfo(benefitProductId: number, event?: Event): void {
    const me = this;
    event.stopPropagation();
    me.benefitProduct = me.getBenefitProduct(benefitProductId);
    me.pushChanges();
  }
  //#endregion

  //#region UI Display Logic

  /** Determine if the "Show More" button should be displayed */
  public canDisplayShowMore(benefits: Models.SOTBonusActionBenefit[]) {
    const me = this;
    return benefits && benefits.length > me._LIMIT_BENEFITS;
  }

  /** Check if "Show More" is active for the bonus action at the given index */
  public isShowMore(actionIndex: number): boolean {
    const me = this;
    return me._showMoreActionIndices.indexOf(actionIndex) >= 0;
  }

  /** Visible benefits — all when "Show More" is active, otherwise the first _LIMIT_BENEFITS */
  public visibleBenefits(bonusAction: Models.SOTBonusAction, actionIndex: number): ReadonlyArray<Models.SOTBonusActionBenefit> {
    const me = this;
    const benefits = bonusAction.benefits as ReadonlyArray<Models.SOTBonusActionBenefit>;
    return me.isShowMore(actionIndex) ? benefits : benefits.slice(0, me._LIMIT_BENEFITS);
  }

  /**
   * trackBy for the bonus action list.
   * `index` is required by Angular's TrackByFunction signature (index, item) but stays unused —
   * identity is tracked by the stable ID, not by list position.
   */
  public trackByBonusActionId(index: number, bonusAction: Models.SOTBonusAction): number {
    return bonusAction.ID;
  }

  /**
   * trackBy for the benefit list.
   * `index` is required by Angular's TrackByFunction signature (index, item) but stays unused —
   * identity is tracked by the stable ID, not by list position.
   */
  public trackByBenefitId(index: number, benefit: Models.SOTBonusActionBenefit): number {
    return benefit.ID;
  }

  /** Translated label for the "Show More"/"Show Less" toggle at the given index */
  public getToggleLabel(actionIndex: number): string {
    const me = this;
    const key = me.isShowMore(actionIndex) ? "benefit_show_less" : "benefit_show_more";
    return me._sotService.TranslateKeyToLanguage(me._sotService.GetCurrentLanguage(), key);
  }

  /** Toggle the "Show More" state for the bonus action at the given index */
  public toggleShowMore(actionIndex: number): void {
    const me = this;
    const indexInShowMore = me._showMoreActionIndices.indexOf(actionIndex);
    if (indexInShowMore >= 0) {
      me._showMoreActionIndices.splice(indexInShowMore, 1);
    } else {
      me._showMoreActionIndices.push(actionIndex);
    }
    me.pushChanges();
  }

  //#endregion

  //#region Dialog Actions
  /**
   * Back to benefit list from product details
   */
  public backToBenefitList(): void {
    const me = this;
    me.displayBenefitProductDetails = false;
    me.pushChanges();
  }

  /** Close dialog */
  public closeBonusActionsView() {
    const me = this;
    me.dialogRef.close();
  }
  //#endregion

  //#region Helper Methods

  /** Load and prepare benefit for detail view */
  private prepareBenefitForDetail(bonusAction: Models.SOTBonusAction, benefit: Models.SOTBonusActionBenefit): void {
    const me = this;
    me.benefitProduct = me.getBenefitProduct(benefit.productID);
    me.selectedBonusAction = bonusAction;
    me.selectedBenefit = benefit;
    me.selectedBenefitCost = benefit.costPoints;
  }

  /** Check if basket product is an instance of the given benefit*/
  private _isSameBenefit(basketProduct: Models.SOTBasketProductViewModel, benefit: Models.SOTBonusActionBenefit): boolean {
    return basketProduct.benefitId === benefit.ID && basketProduct.bonusactionId === benefit.bonusActionID;

  }

  private pushChanges(): void {
    const me = this;
    try {
      me._changeDetectorRef.detectChanges();
    } catch (e) {
      me._sotService.coreLogService.error("BonusActionsDialogComponent.pushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }
  //#endregion

}
